import { useEffect, useState } from "react";
import { api, INR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { PiggyBank, Sparkles, CheckCircle2 } from "lucide-react";

const CHIPS = [500, 1000, 2500, 5000];
const FREQS = [
  { v: "DAILY", label: "Daily" },
  { v: "WEEKLY", label: "Weekly" },
  { v: "MONTHLY", label: "Monthly" },
];

export default function Save() {
  const [enrollments, setEnrollments] = useState([]);
  const [amount, setAmount] = useState(1000);
  const [freq, setFreq] = useState("MONTHLY");
  const [name, setName] = useState("Family Kitty");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/kitty/list");
    setEnrollments(data.enrollments || []);
  };
  useEffect(() => { load(); }, []);

  const enroll = async () => {
    setLoading(true);
    try {
      await api.post("/kitty/enroll", {
        plan_name: name || "My Kitty",
        frequency: freq,
        amount_inr: Number(amount),
      });
      toast.success("Kitty enrolled");
      setName("Family Kitty"); setAmount(1000);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const contribute = async (id) => {
    try {
      const { data } = await api.post(`/kitty/${id}/contribute`);
      toast.success(`Added ${data.grams_added.toFixed(4)}g gold`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="pt-6">
      <div className="text-xs font-bold tracking-[0.22em] uppercase text-brand-primary">Save · Kitty</div>
      <h1 className="mt-1 font-display text-4xl font-semibold text-brand-charcoal">Grow your <em className="italic text-brand-primary">shreedhan</em></h1>
      <p className="mt-2 text-brand-slate">A digital SIP in real gold. Start small, dream big.</p>

      {/* Create kitty */}
      <div data-testid="kitty-create-card" className="mt-6 rounded-3xl border border-brand-line bg-brand-sand p-5 shadow-soft space-y-4">
        <div>
          <Label className="text-brand-charcoal text-sm font-semibold">Kitty name</Label>
          <Input data-testid="kitty-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12 rounded-xl bg-brand-ivory border-brand-line" />
        </div>

        <div>
          <Label className="text-brand-charcoal text-sm font-semibold">Amount per installment</Label>
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {CHIPS.map((c) => (
              <button
                key={c}
                data-testid={`kitty-chip-${c}`}
                onClick={() => setAmount(c)}
                className={`shrink-0 h-11 px-4 rounded-full text-sm font-semibold border transition-transform active:scale-95 ${
                  Number(amount) === c
                    ? "bg-brand-primary text-brand-ivory border-brand-primary"
                    : "bg-brand-ivory text-brand-charcoal border-brand-line"
                }`}
              >
                {INR(c)}
              </button>
            ))}
          </div>
          <Input
            data-testid="kitty-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-3 h-12 rounded-xl bg-brand-ivory border-brand-line tabular-nums"
          />
        </div>

        <div>
          <Label className="text-brand-charcoal text-sm font-semibold">Frequency</Label>
          <RadioGroup value={freq} onValueChange={setFreq} className="mt-2 grid grid-cols-3 gap-2">
            {FREQS.map((f) => (
              <label
                key={f.v}
                data-testid={`kitty-freq-${f.v}`}
                className={`cursor-pointer h-11 rounded-full flex items-center justify-center gap-2 text-sm font-semibold border transition ${
                  freq === f.v ? "bg-brand-primary text-brand-ivory border-brand-primary" : "bg-brand-ivory text-brand-charcoal border-brand-line"
                }`}
              >
                <RadioGroupItem value={f.v} className="sr-only" /> {f.label}
              </label>
            ))}
          </RadioGroup>
        </div>

        <Button
          data-testid="kitty-enroll"
          disabled={loading}
          onClick={enroll}
          className="w-full h-14 rounded-full bg-brand-primary hover:bg-brand-primary-soft text-brand-ivory font-semibold text-base shadow-card active:scale-[0.98]"
        >
          <Sparkles className="mr-2" size={18} /> {loading ? "Enrolling…" : "Enroll Kitty"}
        </Button>
      </div>

      {/* List */}
      <h2 className="mt-8 font-display text-2xl text-brand-charcoal">Your kitties</h2>
      <div data-testid="kitty-list" className="mt-3 space-y-3">
        {enrollments.length === 0 && (
          <div className="text-brand-slate text-sm">No kitties yet. Start your first one above.</div>
        )}
        {enrollments.map((k) => (
          <div key={k.id} className="rounded-2xl border border-brand-line bg-brand-ivory p-4 shadow-soft">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-xl text-brand-charcoal">{k.plan_name}</div>
                <div className="text-xs text-brand-slate mt-0.5">{k.frequency} · {INR(k.amount_inr)}</div>
              </div>
              <PiggyBank className="text-brand-primary" />
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <div className="text-brand-slate">Accumulated</div>
              <div className="tabular-nums font-semibold text-brand-charcoal">{Number(k.accumulated_grams || 0).toFixed(4)}g</div>
            </div>
            <div className="mt-1 flex items-center justify-between text-sm">
              <div className="text-brand-slate">Installments paid</div>
              <div className="tabular-nums font-semibold text-brand-charcoal flex items-center gap-1">
                {k.installments_paid || 0} <CheckCircle2 size={14} className="text-brand-secondary" />
              </div>
            </div>
            <Button
              data-testid={`kitty-contribute-${k.id}`}
              onClick={() => contribute(k.id)}
              variant="outline"
              className="mt-3 w-full h-11 rounded-full border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-brand-ivory"
            >
              Pay this installment
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
